import { useEffect, useState } from "react"
import { FaChevronLeft } from "react-icons/fa"
import { useSearchParams } from "react-router-dom"

import { TTemplateEvent } from "@letshang/constants"

import { templateCategories } from "@fuse/pages/eventTemplates"
import { TemplateCard } from "@fuse/pages/eventTemplates"
import { staticTemplates } from "@fuse/pages/eventTemplates/staticTemplates"

const SkeletonCard = () => (
  <div className="min-w-[300px] max-w-[300px] aspect-square mx-2 first:ml-0 rounded-lg overflow-hidden shadow-lg h-full flex flex-col bg-gray-700 animate-pulse">
    <div className="h-full w-full bg-gray-600"></div>
    <div className="p-4 bg-gradient-to-t from-gray-700 to-transparent">
      <div className="h-6 w-3/4 bg-gray-600 rounded"></div>
    </div>
  </div>
)

const CategorySection = ({
  title,
  description,
  templates,
  onTemplateClick,
  showSkeleton = false,
}: {
  title: string
  description: string
  templates: TTemplateEvent[]
  onTemplateClick: (template: TTemplateEvent) => void
  showSkeleton?: boolean
}) => {
  return (
    <div className="mb-12">
      <h2 className="text-2xl text-white text-left font-semibold mb-2">
        {title} Events
      </h2>
      <p className="text-white/50 text-left mb-6">{description}</p>
      <div className="overflow-x-auto pb-4 -mx-4 px-4 no-scrollbar">
        <div className="flex space-x-4">
          {templates.map((template, index) => (
            <TemplateCard
              key={template?.eventId || index}
              template={template}
              onTemplateClick={onTemplateClick}
            />
          ))}
          {showSkeleton &&
            [...Array(5)].map((_, index) => (
              <SkeletonCard key={`skeleton-${index}`} />
            ))}
        </div>
      </div>
    </div>
  )
}

const EventTemplatesSkeleton = ({
  onTemplateClick,
}: {
  onTemplateClick: (template: TTemplateEvent) => void
}) => {
  const [params] = useSearchParams()
  const [currentCategory, setCurrentCategory] = useState("")
  const categoryFromUrl = params.get("category")

  useEffect(() => {
    if (categoryFromUrl) {
      setCurrentCategory(
        categoryFromUrl?.charAt(0)?.toUpperCase() + categoryFromUrl?.slice(1),
      )
    }
  }, [categoryFromUrl])

  const subCategories = templateCategories[currentCategory] || []

  return (
    <div className="min-h-screen">
      <div className="fixed top-0 left-0 right-0 z-20">
        <div className="absolute inset-0 bg-[#2525258C] backdrop-blur-md"></div>
        <div className="relative flex items-center px-4 md:px-12 py-4 md:py-4">
          <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center bg-white/10">
            <span className="text-white">
              <FaChevronLeft color="white" size={20} />
            </span>
          </div>
          <h1 className="ml-4 text-2xl md:text-3xl text-white font-semibold">
            Event Templates
          </h1>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-8 mt-20 md:mt-32">
        {subCategories?.map((subCategory: string) => {
          const templates =
            staticTemplates.groupedTemplates[currentCategory]?.filter(
              (template: TTemplateEvent) =>
                template.tags?.some(
                  (tag: string) =>
                    tag.toLowerCase() === subCategory.toLowerCase(),
                ),
            ) || []

          return (
            <CategorySection
              key={subCategory}
              title={subCategory}
              description={`Create sacred moments through ${subCategory} traditions and celebrations.`}
              templates={templates}
              onTemplateClick={onTemplateClick}
              showSkeleton={true}
            />
          )
        })}
      </div>
    </div>
  )
}

export default EventTemplatesSkeleton
